<script setup lang="ts">
import { DynamicIcon } from '@/components/primitives'
import { Button, AddIcon, DeleteIcon, RenameIcon } from '@tenorlab/vue-dashboard'
import { useComputedProps } from '../menu-items-container/use-computed-props'
import type { INavigationItem } from '@/models'
import type { TMenuItemContainerProps } from '../menu-items-container/models'

const props = withDefaults(defineProps<TMenuItemContainerProps>(), {
  isCollapsed: false,
})

const emits = defineEmits<{
  (e: 'menuItemClicked', params: INavigationItem): any
  (e: 'renameClick', params: INavigationItem): any
  (e: 'deleteClick', params: INavigationItem): any
  (e: 'addClick'): any
  (e: 'expandOrCollapse', params: INavigationItem): any
}>()

const {
  hasChildren,
  iconCss,
  badgeValueCss,
  menuItemCssClass,
  cssClassExpandedIndicator,
  childButtonCssClass,
} = useComputedProps(props)

const onClick = () => {
  emits('menuItemClicked', props.model)
}

const onChildClick = (e: MouseEvent, childItem: INavigationItem) => {
  e.stopImmediatePropagation()
  e.stopPropagation()
  e.preventDefault()
  emits('menuItemClicked', childItem)
}

const onChildDeleteClick = (e: any, childItem: INavigationItem) => {
  e?.stopPropagation()
  e?.preventDefault()
  emits('deleteClick', childItem)
}

const onChildRenameClick = (e: any, childItem: INavigationItem) => {
  e?.stopPropagation()
  e?.preventDefault()
  emits('renameClick', childItem)
}

const onAddClick = (e: any) => {
  e?.stopPropagation()
  e?.preventDefault()
  emits('addClick')
}

const onExpandClick = () => {
  emits('expandOrCollapse', props.model)
  // nextTick(() => {
  //   if (internalState.isExpanded && props.model.children?.length) {
  //     onChildClick(props.model.children[0])
  //   }
  // })
}

// class="group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium"
</script>

<template>
  <div
    v-if="!hasChildren"
    :class="menuItemCssClass"
    :aria-current="model.selected ? 'page' : undefined"
    @click.stop="onClick"
  >
    <div :class="`flex items-center ${!isCollapsed ? 'gap-3' : ''}`">
      <DynamicIcon v-if="model.icon" :name="model.icon" :cssClass="iconCss" aria-hidden="true" />
      <span :class="isCollapsed ? 'sr-only' : 'flex-1'">
        {{ model.name }}
      </span>
    </div>
    <span v-if="!isCollapsed && model.badgeValue" :class="badgeValueCss">{{ model.badgeValue }}</span>
  </div>
  <div v-if="hasChildren" class="flex flex-col w-full">
    <button
      type="button"
      :class="menuItemCssClass"
      aria-controls="sub-menu-1"
      aria-expanded="false"
      @click.prevent="onExpandClick"
    >
      <div :class="`flex items-center ${!isCollapsed ? 'gap-3' : ''}`">
        <DynamicIcon v-if="model.icon" :name="model.icon" :cssClass="iconCss" aria-hidden="true" />
        <div class="flex items-center gap-1">
          <span :class="isCollapsed ? 'sr-only' : 'flex-1'">
            {{ model.name }}
          </span>
        </div>
        <!-- badge -->
        <span v-if="!isCollapsed && !model.isExpanded && model.badgeValue" :class="badgeValueCss">{{
          model.badgeValue
        }}</span>
      </div>
      <!-- Expanded indicator -->
      <svg v-if="!isCollapsed" :class="cssClassExpandedIndicator" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
      </svg>
    </button>
    <!-- Expandable section -->
    <div
      v-show="model.isExpanded"
      id="sub-menu-1"
      :class="
        !model.isExpanded
          ? 'hidden'
          : isCollapsed
            ? 'border border-primary border-opacity-70 rounded-md'
            : 'flex flex-col gap-1'
      "
    >
      <a
        v-for="childItem in model.children"
        :key="childItem.id"
        :class="
          isCollapsed ? 'flex flex-col cursor-pointer ml-0 p-1' : 'flex flex-col cursor-pointer ml-7 p-0'
        "
        aria-controls="sub-menu-1"
        aria-expanded="false"
        :aria-current="childItem.selected ? 'page' : undefined"
        @click="(ev) => onChildClick(ev, childItem)"
      >
        <div :class="childButtonCssClass({ selected: childItem.selected } as any)">
          <DynamicIcon
            v-if="childItem.icon"
            :name="childItem.icon"
            :cssClass="`shrink-0 ${isCollapsed ? 'size-3' : 'size-4'}`"
            aria-hidden="true"
          />
          <span :class="isCollapsed ? 'sr-only' : 'flex-1'">
            {{ childItem.name }}
          </span>
          <div v-if="!isCollapsed && `${childItem.id}` !== 'default'" class="flex items-center gap-2">
            <Button
              :data-testid="`rename-dashboard_${childItem.id}`"
              :isIconButton="true"
              class="size-4 hidden group-hover:flex"
              :tooltip="{
                placement: 'bottom',
                title: 'Rename Dashboard',
              }"
              @click="(ev: any) => onChildRenameClick(ev, childItem)"
            >
              <RenameIcon />
            </Button>
            <Button
              :data-testid="`delete-dashboard_${childItem.id}`"
              :isIconButton="true"
              class="text-danger size-4 hidden group-hover:flex"
              :tooltip="{
                placement: 'bottom',
                title: 'Delete Dashboard',
              }"
              @click="(ev: any) => onChildDeleteClick(ev, childItem)"
            >
              <DeleteIcon />
            </Button>
          </div>
        </div>
      </a>

      <a
        key="add-dashboard"
        :class="
          isCollapsed ? 'flex flex-col cursor-pointer ml-0 p-1' : 'flex flex-col cursor-pointer ml-7 p-0'
        "
        aria-controls="sub-menu-1"
        aria-expanded="false"
      >
        <div :class="childButtonCssClass({ selected: false } as any)" @click="onAddClick">
          <AddIcon :class="`shrink-0 ${isCollapsed ? 'size-3' : 'size-4'}`" aria-hidden="true" />
          <span :class="isCollapsed ? 'sr-only' : 'flex-1'">New</span>
        </div>
      </a>
    </div>
  </div>
</template>
